import type { ReactNode } from 'react';
import { X } from '@phosphor-icons/react';
import { Tip } from '~/components/ui/tip';
import { cn } from '~/lib/utils';

/**
 * The Build window's ONE cog (eddy, 2026-10-26: "the settings for eddy
 * builder should not be a full modal. we only want one cog and have all the
 * visibility settings etc there"). The header keeps Preview, this cog and the
 * three window buttons; everything else that used to sit beside them — who
 * can open the app, Save App, Configuration, the "…" menu's project actions,
 * the Builder preferences doorway — lives in this panel.
 *
 * A panel INSIDE the window, docked to its right edge under the header, never
 * a modal: no scrim, the conversation stays readable and usable beside it,
 * and closing it (×, the cog again, or Escape) leaves the window as it was.
 */
export function BuildSettingsPanel({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    // FRONT-3223 (eddy, 2026-10-28): a full column beside the Build's content,
    // not an overlay over it — the window's body makes room for it.
    <aside
      aria-label="Build settings"
      data-testid="build-settings-panel"
      className="flex h-full w-[400px] max-w-full shrink-0 flex-col bg-card"
      style={{ animation: 'eddy-build-settings-in 180ms ease-out' }}
    >
      <style>{`
        @keyframes eddy-build-settings-in {
          from { opacity: 0; transform: translateX(12px) }
          to   { opacity: 1; transform: translateX(0) }
        }
      `}</style>
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border pl-4 pr-2">
        <h2 className="min-w-0 flex-1 truncate text-[length:var(--text-sm)] font-semibold">{title}</h2>
        <Tip label="Close settings (Esc)">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close settings"
            className="inline-flex size-8 shrink-0 items-center justify-center rounded-[10px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X size={16} />
          </button>
        </Tip>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 py-4">{children}</div>
    </aside>
  );
}

export function PanelSection({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <div>
        <h3 className="text-[length:var(--text-xs)] font-semibold uppercase tracking-[0.06em] text-muted-foreground">{title}</h3>
        {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

/** One action row: icon, label, and an optional explanation under it. */
export function PanelAction({
  icon,
  label,
  hint,
  onClick,
  disabled,
  destructive,
  testId,
}: {
  icon: ReactNode;
  label: string;
  hint?: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cn(
        'flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-50',
        destructive ? 'text-destructive' : 'text-foreground',
      )}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span className="min-w-0">
        <span className="block text-[length:var(--text-sm)] font-medium">{label}</span>
        {hint && <span className="mt-0.5 block text-xs text-muted-foreground">{hint}</span>}
      </span>
    </button>
  );
}
